import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { RichSession } from "../../../redux/reducers/scheduleReducer";
import { getSessionStatusColor } from "./utils";

interface Props {
  session: RichSession;
  onClick: () => void;
  selected?: boolean;
  drawerOpen?: boolean;
}

const SessionChip: React.FC<Props> = ({
  session,
  onClick,
  selected = false,
  drawerOpen = false,
}) => {
  if (session.is_phantom) {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          height: "22px",
          px: 0.75,
          borderRadius: "11px",
          border: "1px dashed #9e9e9e",
          color: "#616161",
          fontSize: "0.7rem",
          cursor: "pointer",
          width: "fit-content",
          maxWidth: "80px",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.7rem",
            lineHeight: 1,
          }}
        >
          phantom
        </Typography>
      </Box>
    );
  }

  const label = session.s?.conversation_id
    ? `conv:${session.s.conversation_id.slice(0, 50)}`
    : session.s?.session_id.slice(0, 50) ?? "session";

  const backgroundColor = session.s?.conversation_id ? "#1976d2" : "#42a5f5";
  const hoverColor = session.s?.conversation_id ? "#1565c0" : "#2196f3";

  const userEmail = session.s?.user_email || null;
  const status = session.s?.status;
  const statusColor = status ? getSessionStatusColor(status) : "#95a5a6";

  // Always show email or a placeholder to maintain consistent sizing
  const displayEmail = userEmail || "no email";

  const chipContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: "32px",
        px: 1.5,
        py: 0.5,
        mx: 0.5, // Minimal side padding
        borderRadius: "16px",
        backgroundColor: selected ? "#1565c0" : backgroundColor,
        color: "#fff",
        cursor: "pointer",
        width: selected || drawerOpen ? "60%" : { xs: "60%", lg: "80%" },
        maxWidth: selected || drawerOpen ? "60%" : { xs: "60%", lg: "80%" },
        minWidth: "0",
        position: "relative",
        boxShadow: selected
          ? "0 4px 12px rgba(21, 101, 192, 0.3), 0 0 0 2px #1976d2"
          : "0 2px 6px rgba(0,0,0,0.12)",
        transition: "all 0.2s ease-in-out",
        border: selected
          ? "2px solid #1976d2"
          : "2px solid rgba(255, 255, 255, 0.3)",
        "&:hover": {
          backgroundColor: selected ? "#1565c0" : hoverColor,
          transform: "translateY(-1px)",
          boxShadow: selected
            ? "0 6px 16px rgba(21, 101, 192, 0.4), 0 0 0 2px #1976d2"
            : "0 4px 12px rgba(0,0,0,0.18)",
        },
        "&:active": {
          transform: "translateY(0px)",
        },
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {/* Main content area */}
      <Box
        sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.7rem",
            lineHeight: 1.1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 600,
            mb: 0.2,
          }}
        >
          {label}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            fontSize: "0.65rem",
            lineHeight: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            opacity: displayEmail === "no email" ? 0.6 : 0.85,
            fontWeight: 400,
            fontStyle: displayEmail === "no email" ? "italic" : "normal",
          }}
        >
          {displayEmail}
        </Typography>
      </Box>

      {/* Status indicator */}
      {status && (
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: statusColor,
            border: "2px solid rgba(255, 255, 255, 0.9)",
            ml: 1.5,
            flexShrink: 0,
            boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
          }}
        />
      )}
    </Box>
  );

  // Wrap with tooltip if we have full email or status info
  if (userEmail || status) {
    return (
      <Tooltip
        title={
          <Box>
            {userEmail && (
              <Typography variant="caption" display="block">
                Email: {userEmail}
              </Typography>
            )}
            {status && (
              <Typography variant="caption" display="block">
                Status: {status}
              </Typography>
            )}
          </Box>
        }
        placement="top"
        arrow
      >
        {chipContent}
      </Tooltip>
    );
  }

  return chipContent;
};

export default SessionChip;
